import { Box, Flex, Image, Text } from '@chakra-ui/react';
import styled from '@emotion/styled/macro';

import { Carousel } from 'components';
import { rgba } from 'polished';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { mq } from 'styles/breakpoints';
import colors from 'themes/default/common/colors';
import { rem } from 'utils/fonts';
import { getCountryWithCity } from 'utils/project';

//region styled

const TagWrapper = styled(Flex)`
  margin-bottom: 30px;

  div {
    margin-right: 10px;
  }
`;

const Title = styled(Text)`
  font-weight: bold;
  font-size: ${rem('24px')};
  color: ${colors.pink['500']};

  ${mq.lg} {
    font-size: ${rem('26px')};
  }

  ${mq.xl} {
    font-size: ${rem('32px')};
  }
`;
const ProjectSubtitle = styled(Text)`
  color: ${rgba(colors.gray[900], 0.5)};
  font-size: ${rem('18px')};
  font-weight: 500;
  margin-bottom: 20px;

  ${mq.lgMinMax} {
    font-size: ${rem('16px')};
  }
`;

const StyledImage = styled(Box)<{ url?: string; carousel?: boolean }>`
  background-image: url(${(props): string | undefined => props.url});
  background-size: cover;
  height: 190px;
  border-radius: 6px;
  margin-bottom: 35px;
  position: relative;

  ${mq.lg} {
    width: 50%;
    margin-right: 30px;
    margin-bottom: ${(props): string => (props.carousel ? '74px' : '35px')};
    .slick-dots {
      bottom: -45px;
    }
  }
`;

const StyledLink = styled.a`
  width: 116px;
  height: 36px;
  background-color: ${rgba(colors.pink[200], 0.4)};
  border-radius: 48px;
  color: ${colors.pink[500]};
  font-weight: 600;
  font-size: ${rem(13)};
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;

  ${mq.lgMinMax} {
    width: 96px;
    font-size: ${rem(11)};
  }
`;

const HeaderWrapper = styled(Flex)`
  ${mq.lgMax} {
    flex-direction: column;
  }
`;

const TitleWrapper = styled.div``;

//endregion

const ProjectHeader: FC<{ project?: Project }> = ({ project }) => {
  const { t } = useTranslation('projects');

  return (
    <HeaderWrapper>
      <StyledImage url={project?.imageUrl ? `${project?.imageUrl}?q=100&h=294&auto=format` : ''} carousel={!!project?.additionalImages?.length}>
        <Carousel>
          <Image
            src={project?.imageUrl ? `${project?.imageUrl}?q=100&h=194&auto=format` : ''}
            alt=""
            h="194px"
            borderRadius="6px"
            objectFit="cover"
          />
          {project?.additionalImages?.map(img => (
            <Image src={`${img.url}?q=90&h=294&auto=format`} alt={img.alt} key={img.url} h="194px" borderRadius="6px" objectFit="cover" />
          ))}
        </Carousel>
      </StyledImage>
      <TitleWrapper>
        <Title as="h1" fontFamily="heading">
          {project?.title}
        </Title>
        <ProjectSubtitle>{getCountryWithCity(project)}</ProjectSubtitle>
        <TagWrapper>
          {project?.factUrl && (
            <StyledLink href={project?.factUrl} target="_blank" rel="noopener noreferrer">
              {t('facts')}
            </StyledLink>
          )}
          <StyledLink
            //URL based on https://stackoverflow.com/questions/47038116/google-maps-url-with-pushpin-and-satellite-basemap
            href={`https://www.google.com/maps?t=k&q=loc:${project?.location.lat}+${project?.location.lng}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('map')}
          </StyledLink>
        </TagWrapper>
      </TitleWrapper>
    </HeaderWrapper>
  );
};

export default ProjectHeader;
